import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material'
import React, { useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { ReliabilityIntelligenceIcon } from 'components/common/ReliabilityIntelligenceIcon'
import {
  type StatusCheckFormValues,
  STATUS_CHECK_CATEGORIES
} from 'components/status-checks/status-checks-form-utils'
import {
  ObservabilityToolType,
  StatusCheckCategory
} from 'types/api/status-checks/status-check'

const FIELD_ID = 'status-check-category-field'
const FIELD_LABEL = 'Category'
const FORM_CONTROL_WIDTH = 240

export const REQUIRED_FIELD_LABEL =
  'Choose the category that best describes what this health check monitors.'
export const RELIABILITY_INTELLIGENCE_FIELD_LABEL =
  'For increased accuracy of Reliability Intelligence results, choose the category that best describes what this health check monitors.'

export const StatusCheckCategoryField: React.FC<{
  isRequired: boolean
}> = ({ isRequired }) => {
  const { control } = useFormContext<StatusCheckFormValues>()

  const observabilityTool = useWatch({
    control,
    name: 'observabilityTool'
  })

  const isDynatraceObservabilityTool =
    observabilityTool.type === ObservabilityToolType.DYNATRACE

  // If the observability tool is Dynatrace, we exclude the "Unknown" category option
  const statusCheckCategories = useMemo(
    () =>
      isDynatraceObservabilityTool
        ? STATUS_CHECK_CATEGORIES.filter(
            ([key]) => key !== StatusCheckCategory.UNKNOWN
          )
        : STATUS_CHECK_CATEGORIES,
    [isDynatraceObservabilityTool]
  )

  return (
    <Stack sx={{ rowGap: 2 }}>
      <Stack direction="row" sx={{ columnGap: 0.5 }}>
        {isRequired ? (
          <Typography>{REQUIRED_FIELD_LABEL}</Typography>
        ) : (
          <>
            <ReliabilityIntelligenceIcon />
            <Typography>{RELIABILITY_INTELLIGENCE_FIELD_LABEL}</Typography>
          </>
        )}
      </Stack>
      <FormControl sx={{ width: FORM_CONTROL_WIDTH }}>
        <InputLabel id={FIELD_ID}>{FIELD_LABEL}</InputLabel>
        <Controller
          control={control}
          name="category"
          render={({ field }) => {
            return (
              <Select
                {...field}
                required
                label={FIELD_LABEL}
                labelId={FIELD_ID}
                variant="outlined"
              >
                {statusCheckCategories.map(([key, label]) => {
                  return (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  )
                })}
              </Select>
            )
          }}
        />
      </FormControl>
    </Stack>
  )
}
